import { repRange } from './formatters.js';
// Pure builder for an exercise card in the logger (Bigger Leaner Stronger, 4th ed.).
// Work sets start at the weight the progression engine left for this lift on this
// workout day (pe.start_weight_lb, kept current as sets are logged). Rep boxes start
// EMPTY so a top-rep set is always a deliberate entry, never a pre-typed one.
// Options: warmup (first lift for its muscle group today: two warm-up sets, p.330)
// and deload (the 9th week of a phase: 2 sets, 2 or 4 reps, p.332).

import { totalReps, warmupSets } from './bls-engine.js';
import { DELOAD_SETS, DELOAD_REPS_PRIMARY, DELOAD_REPS_ACCESSORY } from './phase.js';

export function buildExerciseCard(pe, lastSets = [], { warmup = false, deload = false } = {}) {
  const tracking = pe.tracking || pe.exercise?.tracking || 'weight';
  const work = lastSets
    .filter((s) => !s.is_warmup)
    .sort((a, b) => a.set_number - b.set_number);
  const nSets = deload ? Math.min(DELOAD_SETS, pe.target_sets || DELOAD_SETS) : pe.target_sets;
  const isPrimary = pe.exercise?.is_primary ?? (pe.rep_high <= 6);

  // Bodyweight (reps, optional added load) and timed (seconds) skip the weight engine.
  if (tracking !== 'weight') {
    const lastLoad = work.length ? Math.max(...work.map((s) => Number(s.weight_lb) || 0)) : 0;
    const sets = Array.from({ length: nSets }, (_, i) => ({
      setNumber: i + 1,
      weight: tracking === 'bodyweight' && lastLoad > 0 ? lastLoad : null,
      reps: tracking === 'time' ? (work.length ? (work[0].reps ?? pe.rep_high) : pe.rep_high) : null, // seconds prefill the timer
      done: false, isWarmup: false,
    }));
    const lastSummary = work.length
      ? (tracking === 'time' ? work.map((s) => s.reps).join('s, ') + 's' : work.map((s) => s.reps).join(', ') + ' reps')
      : 'first time';
    const coach = tracking === 'time'
      ? `Hold ${pe.rep_low}-${pe.rep_high}s per set. Tap the timer to count it down.`
      : `Bodyweight: stop about 1 rep shy of failure (p.257). Aim for ${repRange(pe.rep_low, pe.rep_high)}.`;
    return {
      name: pe.exercise.name, tracking, isPrimary,
      repLow: pe.rep_low, repHigh: pe.rep_high, restSeconds: pe.rest_seconds,
      increment: pe.increment_lb, required: 1, knee: false, deload,
      lastWeight: lastLoad || null, startWeight: null, carryFrom: null, sets, lastSummary, coach,
      lastTotal: totalReps(work), addWeight: tracking === 'bodyweight' && lastLoad > 0,
    };
  }

  const lastTop = work.length ? Math.max(...work.map((s) => Number(s.weight_lb) || 0)) : null;
  const startWeight = pe.start_weight_lb ?? (work.length ? Number(work[work.length - 1].weight_lb) : null);
  const required = Math.min(3, Math.max(1, Number(pe.progression_mode) || 1));
  const lastTotal = totalReps(work);

  const deloadReps = isPrimary ? DELOAD_REPS_PRIMARY : DELOAD_REPS_ACCESSORY;
  const workSets = Array.from({ length: nSets }, () => ({
    setNumber: 0, weight: startWeight, reps: deload ? deloadReps : null, done: false, isWarmup: false,
  }));
  const warm = warmup ? warmupSets(startWeight).map((w) => ({ ...w, setNumber: 0, done: false })) : [];
  const sets = [...warm, ...workSets].map((s, i) => ({ ...s, setNumber: i + 1 }));

  const lastSummary = work.length
    ? (new Set(work.map((s) => Number(s.weight_lb))).size === 1
      ? `${work[0].weight_lb} × ${work.map((s) => s.reps).join(', ')}`
      : work.map((s) => `${s.weight_lb}×${s.reps}`).join(', '))
    : 'first time';

  const left = isPrimary ? '1-2 good reps' : 'a rep';
  let coach;
  if (deload) coach = `Deload week: ${nSets} sets at your normal weight, only ${deloadReps} reps each. Easy on purpose (p.332).`;
  else if (required > 1) coach = `Get ${required} sets of ${pe.rep_high} with ${left} left in this workout, then add ${pe.increment_lb} lb (p.327).`;
  else if (work.length) coach = `${pe.rep_high} reps with ${left} left adds ${pe.increment_lb} lb on your next set. Otherwise beat last time's ${lastTotal} total reps (p.327, 345).`;
  else coach = `${pe.rep_high} reps with ${left} left adds ${pe.increment_lb} lb on your next set (p.327).`;

  return {
    name: pe.exercise.name,
    tracking: 'weight',
    isPrimary,
    repLow: pe.rep_low,
    repHigh: pe.rep_high,
    restSeconds: pe.rest_seconds,
    increment: pe.increment_lb,
    required,
    carryFrom: pe.carry_from_lb ?? null,
    knee: pe.knee,
    deload,
    lastWeight: lastTop,
    startWeight,
    lastTotal,
    sets,
    lastSummary,
    coach,
  };
}

// Aggregate a finished workout into a plain summary for the celebration screen.
// Counts only completed work sets (warm-ups and unchecked sets don't count),
// and rolls every go_up result into "+N lb on M lifts".
export function summarizeFinish(cards = []) {
  let workSets = 0;
  let exercisesDone = 0;
  let earned = 0;
  let drops = 0;
  const goUps = [];
  const prs = [];

  for (const e of cards) {
    const sets = e.card?.sets || [];
    const doneWork = sets.filter((s) => s.done && !s.isWarmup);
    workSets += doneWork.length;
    if (doneWork.length) exercisesDone += 1;

    // A PR = logged heavier than the prior session's top work weight.
    const weights = doneWork.map((s) => s.weight).filter((w) => Number.isFinite(w));
    const maxW = weights.length ? Math.max(...weights) : null;
    const lastW = e.card?.lastWeight;
    if (maxW != null && lastW != null && maxW > lastW) prs.push({ name: e.card?.name || 'Lift', weight: maxW });

    const r = e.result;
    if (r?.decision === 'go_up') {
      const lb = (r.toWeight ?? 0) - (r.fromWeight ?? 0);
      goUps.push({ name: e.card?.name || 'Lift', lb });
      earned += lb;
    } else if (r?.decision === 'drop') {
      drops += 1;
    }
  }

  const headline = goUps.length
    ? `You earned +${earned} lb on ${goUps.length} lift${goUps.length > 1 ? 's' : ''}.`
    : null;

  return { exercisesDone, workSets, goUps, earned, drops, prs, headline };
}

// True when this exercise's heaviest done work set beats the prior top weight.
export function isPersonalRecord(card) {
  const weights = (card?.sets || []).filter((s) => s.done && !s.isWarmup).map((s) => s.weight).filter((w) => Number.isFinite(w));
  if (!weights.length || card?.lastWeight == null) return false;
  return Math.max(...weights) > card.lastWeight;
}
